import {Outlet, useLoaderData, useOutletContext} from '@remix-run/react'
import {json, type LoaderFunction, type MetaFunction, redirect, type TypedResponse} from '@remix-run/node'
import type {SitemapFunction} from 'remix-sitemap'
import {textToSlug} from '@roundforest/text-transforms-commons'
import type {RootContext, SlugLoaderResponse, SortingOption} from '~/types'
import {fetchData, fetchFakeData} from '~/api/slug-data-fetcher.server'
import {getTranslation} from '~/localization/translation'
import {setEnvironmentWith} from '~/utils/async-storage.server'
import {getDomainConfig} from '~/domains'
import {pinoLogger} from '~/entry.server'
import {getCategories, getFakeCategories} from '~/api/search-categories-fetcher.server'
import {dwsSellersClient, metacriticProductCatalogClient} from '~/api/clients.server'
import {isLevantaVariation} from '~/utils/list-variation'
import {CATEGORIES_FOR_INDEX_BY_DOMAIN_NAME} from '../../../server/middleware/extend-request'
import {sendOutOfScopeRequest} from '../../../server/services/sqs'
import {
  getCachedSlugResponse,
  incrementSlugResponseCacheHit,
  incrementSlugResponseCacheMiss,
  makeSlugFetchSingleFlightKey,
  recordSlugResponseLoaderDuration,
  setCachedSlugResponse,
} from '../../../server/services/slug-response-cache'
import {runSingleFlight} from '../../../server/services/single-flight'

export const sitemap: SitemapFunction = async (slug) => {
  return {
    loc: `/${slug}`,
  }
}

const BLACK_FRIDAY_META_TITLE_LOCALES = ['']
const CYBER_MONDAY_META_TITLE_LOCALES = ['']

export const meta: MetaFunction<typeof loader> = ({data}: {data: SlugLoaderResponse}) => {
  const locale = data?.locale?.toLowerCase() ?? 'us'
  const language = data?.language ?? 'en-US'
  const slug = data?.dataResponse?.productsCategory?.slug
  const domain = data?.domain
  const {siteName, description, camelCaseUrl, title} = getDomainConfig(domain, locale)
  const categoryName = data?.dataResponse?.productsCategory?.name ?? 'category name'
  const productsCount = Math.min(data?.dataResponse?.products?.length ?? 0, 10)
  const translations = getTranslation(language)
  const metaDescription = description(categoryName, productsCount?.toString())

  const getMetaTitle = () => {
    if (BLACK_FRIDAY_META_TITLE_LOCALES.includes(locale)) {
      return translations.BlackFriday.metaTitle(categoryName, productsCount, data?.locale, camelCaseUrl)
    }

    if (CYBER_MONDAY_META_TITLE_LOCALES.includes(locale)) {
      return translations.CyberMonday.metaTitle(categoryName, productsCount, data?.locale, camelCaseUrl)
    }

    return title(categoryName, productsCount?.toString())
  }

  const metaTitle = getMetaTitle()

  return [
    {
      title: metaTitle,
    },
    {
      name: 'description',
      content: metaDescription,
    },
    {
      name: 'charset',
      content: 'utf-8',
    },
    {
      property: 'og:url',
      content: `https://${domain}/${slug}`,
    },
    {
      tagName: 'link',
      rel: 'canonical',
      href: `https://${domain}/${slug}`,
    },

    {
      property: 'og:locale',
      content: locale,
    },
    {
      property: 'og:site_name',
      content: siteName,
    },
    {
      property: 'og:image:width',
      content: '1024',
    },
    {
      property: 'og:image:height',
      content: '1024',
    },
    {
      property: 'og:image',
      content: `${data?.dataResponse?.productsCategory.image}`,
    },
    {
      property: 'og:image:alt',
      content: `${siteName} Image`,
    },
    {
      property: 'og:title',
      content: metaTitle,
    },
    {
      property: 'og:description',
      content: metaDescription,
    },
    {
      property: 'og:type',
      content: 'website',
    },
  ]
}

export const loader: LoaderFunction = async ({
  request,
  context,
  params,
}): Promise<TypedResponse<SlugLoaderResponse | undefined>> => {
  const loaderStartTime = performance.now()
  setEnvironmentWith(context)
  const isProduction = process.env.NODE_ENV === 'production'
  const url = new URL(request.url)
  const {searchParams, pathname} = url
  const {locale, featureFlags, isBot, domain, language, routeName, traffic, urlPath, variation} = context

  const strippedPathname = pathname?.replace(/\/+$/, '')
  const isSingleSegmentPath = strippedPathname.indexOf('/') === strippedPathname.lastIndexOf('/')
  // /lv/<slug>, /lv2/<slug> and /cc/<slug> have two segments but is a list page: a fetch failure must 404, not render the empty page.
  const isListPage = routeName === 'levanta_slug_page' || isSingleSegmentPath

  let searchTermSlug

  if (routeName === 'search_page') {
    const searchQuery = searchParams.get('query') ?? ''
    if (!searchQuery || searchQuery.length === 0) {
      return redirect('/')
    }
    const categoryResponse =
      process.env.NODE_ENV === 'production'
        ? await getCategories({
            query: searchQuery,
            locale,
            limit: 1,
            imageProxyCredentials: {
              host: process.env.IMAGE_PROXY_HOST!,
              key: process.env.IMAGE_PROXY_KEY!,
            },
          })
        : await getFakeCategories({
            locale,
            imageProxyCredentials: {
              host: process.env.IMAGE_PROXY_HOST!,
              key: process.env.IMAGE_PROXY_KEY!,
            },
          })
    if (categoryResponse.type === 'success') {
      searchTermSlug = categoryResponse.value[0].slug
    }
  }

  const slug =
    routeName === 'search_page' && !!searchTermSlug
      ? searchTermSlug
      : textToSlug(params?.slug || '')?.replace('best-', '')
  const isCacheEnabledByFlag = featureFlags?.enableSlugResponseCache === true
  // The slug response cache keys on urlPath, which excludes the query string, so the /search route
  // (whose slug is resolved from ?query=) cannot be represented by a single key — every query would
  // share "/search" and poison/mis-serve each other. Exclude search from the cache entirely.
  const shouldUseRedisCache = isProduction && isCacheEnabledByFlag && routeName !== 'search_page'
  const cacheMetricTags = {
    locale: locale.toLowerCase(),
    traffic: traffic ?? 'unknown',
  }

  const sortBy = searchParams.get('sortBy') as SortingOption | null

  // The LEVANTA list is Levanta products ordered by the normal ranking end to end, so the manual
  // ?asin=/?position= overrides are ignored there (they would force a below-fold item into the top
  // ten and, as a side effect, opt the request out of single-flight coalescing).
  const isLevanta = isLevantaVariation(variation)
  const forcedAsin = isLevanta ? null : searchParams.get('asin')
  const forcedPosition = isLevanta ? null : searchParams.get('position')

  const host = request.headers.get('host') as string
  const {domainName, domainType} = getDomainConfig(host)
  const cacheKeyParts = {
    locale: locale.toLowerCase(),
    domainType,
    urlPath,
    featureFlags,
  }
  if (
    traffic === 'paid' &&
    params?.slug &&
    CATEGORIES_FOR_INDEX_BY_DOMAIN_NAME[domainName] &&
    CATEGORIES_FOR_INDEX_BY_DOMAIN_NAME[domainName].scopes !== null &&
    !CATEGORIES_FOR_INDEX_BY_DOMAIN_NAME[domainName].slugs?.[params.slug]
  ) {
    sendOutOfScopeRequest({
      host,
      domainName,
      slug: params.slug,
      domainScopes: CATEGORIES_FOR_INDEX_BY_DOMAIN_NAME[domainName].scopes,
    }).catch((err) => {
      pinoLogger.error({err}, 'Failed to send out of scope request to SQS')
    })
  }

  // Page version stamp seen on read (null when not read or Redis did not answer). It is stored with
  // the write-back so a stamp bump during the fetch below makes that entry stale.
  let cacheVersion: string | null = null
  let missOutcome: 'miss' | 'stale' = 'miss'
  if (shouldUseRedisCache) {
    const lookup = await getCachedSlugResponse(cacheKeyParts)
    cacheVersion = lookup.version

    if (lookup.outcome === 'hit') {
      const cachedResponse = lookup.response
      // Any cached outcome (success or a cached negative) lets us skip the downstream fan-out.
      incrementSlugResponseCacheHit(cacheMetricTags)
      recordSlugResponseLoaderDuration(performance.now() - loaderStartTime, {
        ...cacheMetricTags,
        cache_outcome: 'hit',
      })

      if (cachedResponse.type === 'success') {
        return json<SlugLoaderResponse>({
          dataResponse: cachedResponse.data,
          locale: locale.toUpperCase(),
          featureFlags,
          slug,
          domain,
          language,
          sortBy,
          isBot,
          forcedPosition,
          forcedAsin,
          variation,
        })
      }

      if (cachedResponse.type === 'empty') {
        return json<SlugLoaderResponse>(buildEmptySlugResponse({locale, featureFlags, slug, domain, language, isBot}))
      }

      // cachedResponse.type === 'not_found'
      throw new Response('Not Found', {status: 404})
    }

    missOutcome = lookup.outcome
    incrementSlugResponseCacheMiss({...cacheMetricTags, reason: lookup.outcome === 'stale' ? 'stale' : 'absent'})
  }

  const runFetchData = () =>
    fetchData(
      {
        metacriticProductCatalogClient,
        dwsSellersClient,
      },
      {
        slug,
        forcedAsin,
        forcedPosition,
        variation: variation ?? 'BASELINE',
      },
      {
        host: process.env.IMAGE_PROXY_HOST!,
        key: process.env.IMAGE_PROXY_KEY!,
      },
    )

  // Single-flight only the cacheable shape (no forced asin/position): concurrent misses for the same
  // slug on this task collapse onto one fan-out instead of stampeding the downstream services.
  // The key must capture everything fetchData's result depends on, since callers share one result:
  //  - the resolved slug (on /search the urlPath is constant "/search" while the slug comes from the
  //    query, so the cache-key parts alone would merge different searches), and
  //  - the domainName (fetchData reads it from AsyncLocalStorage and varies the downstream request,
  //    e.g. includeStoreData = cssDomains.includes(domainName)), which the domainType-based cache key
  //    does not distinguish between sibling domains.
  const canCoalesceFetch = !forcedAsin && !forcedPosition
  const fetchedData = canCoalesceFetch
    ? await runSingleFlight(
        makeSlugFetchSingleFlightKey({cacheKeyParts, domainName, slug, version: cacheVersion}),
        runFetchData,
      )
    : await runFetchData()

  // NOTE: validateSlugScope rejects per-domain (keyed by domainName), but the response cache key is
  // the coarser domainType. Caching this 404 would let one domain's out-of-scope rejection poison a
  // sibling domain (same domainType) whose slug is in scope. So this outcome is intentionally NOT
  // negatively cached — only the slug-data-driven empty/not_found outcomes below are, and those
  // depend on the slug alone (domain-independent), matching the existing success-cache granularity.
  validateSlugScope(slug, domainName, locale, fetchedData, url.href, traffic)

  const data = process.env.NODE_ENV !== 'production' ? await fetchFakeData() : fetchedData

  if (data?.type === 'success') {
    const successPayload: SlugLoaderResponse = {
      dataResponse: data.value,
      locale: locale.toUpperCase(),
      featureFlags,
      slug,
      domain,
      language,
      sortBy,
      isBot,
      forcedPosition,
      forcedAsin,
      variation,
    }

    if (shouldUseRedisCache) {
      // Fire-and-forget: writing back to Redis must not add latency to the response. The call
      // bounds itself with an internal timeout and never throws, so it's safe to leave unawaited.
      void setCachedSlugResponse(
        cacheKeyParts,
        {
          type: 'success',
          data: successPayload.dataResponse,
        },
        {version: cacheVersion},
      )
    }
    recordSlugResponseLoaderDuration(performance.now() - loaderStartTime, {
      ...cacheMetricTags,
      cache_outcome: shouldUseRedisCache ? missOutcome : 'disabled',
    })
    return json<SlugLoaderResponse>(successPayload)
  } else if (!isListPage) {
    if (shouldUseRedisCache && isStableNegativeOutcome(data)) {
      void setCachedSlugResponse(cacheKeyParts, {type: 'empty'}, {version: cacheVersion})
    }
    recordSlugResponseLoaderDuration(performance.now() - loaderStartTime, {
      ...cacheMetricTags,
      cache_outcome: shouldUseRedisCache ? missOutcome : 'disabled',
    })
    return json<SlugLoaderResponse>(buildEmptySlugResponse({locale, featureFlags, slug, domain, language, isBot}))
  } else {
    if (shouldUseRedisCache && isStableNegativeOutcome(data)) {
      void setCachedSlugResponse(cacheKeyParts, {type: 'not_found'}, {version: cacheVersion})
    }
    throw new Response('Not Found', {status: 404})
  }
}

/**
 * Whether a non-success fetchData outcome is a STABLE negative that is safe to cache. Only a
 * definitive 404 ("no such category" / "Empty category!") qualifies. Transient/retryable failures —
 * timeouts (ETIMEDOUT, which carry no HTTP status) and 5xx — must NOT be cached, otherwise a brief
 * backend blip would pin an empty/404 response for the whole negative TTL even after recovery.
 */
function isStableNegativeOutcome(result: unknown): boolean {
  return (result as {error?: {statusCode?: number}} | null | undefined)?.error?.statusCode === 404
}

function buildEmptySlugResponse({
  locale,
  featureFlags,
  slug,
  domain,
  language,
  isBot,
}: {
  locale: string
  featureFlags: SlugLoaderResponse['featureFlags']
  slug: SlugLoaderResponse['slug']
  domain: SlugLoaderResponse['domain']
  language: string
  isBot: SlugLoaderResponse['isBot']
}): SlugLoaderResponse {
  return {
    dataResponse: {
      updatedTimestamp: 111,
      productsCategory: {
        slug: '',
        name: '',
        image: '',
      },
      products: [],
      sponsoredProducts: [],
      relatedProducts: [],
      pageLoadFlags: {},
    },
    sortBy: 'discount',
    locale: locale.toUpperCase(),
    featureFlags,
    slug,
    domain,
    language,
    isBot,
  }
}

const SlugIndex = () => {
  const {dataResponse, locale, featureFlags, slug, isBot, sortBy, forcedPosition, forcedAsin, variation} =
    useLoaderData<SlugLoaderResponse>()
  const {displayCookieConsent, showCookieConsentOnBottom, clickId, updateClickId, setShowCookieConsentGdpr} =
    useOutletContext<RootContext>() ?? {}

  return (
    <Outlet
      key={slug}
      context={{
        dataResponse,
        locale,
        featureFlags,
        slug,
        isBot,
        sortBy,
        displayCookieConsent,
        showCookieConsentOnBottom,
        clickId,
        updateClickId,
        forcedPosition,
        forcedAsin,
        variation,
        setShowCookieConsentGdpr,
      }}
    />
  )
}

type FetchDataResult = Awaited<ReturnType<typeof fetchData>>

function validateSlugScope(
  slug: string,
  domainName: string,
  locale: string,
  fetchedData: FetchDataResult,
  url: string,
  traffic?: string,
): void {
  const domainConfig = CATEGORIES_FOR_INDEX_BY_DOMAIN_NAME[domainName]

  if (!domainConfig?.blockOutOfScope) {
    return
  }

  const productCategoryScopes = fetchedData.type === 'success' ? fetchedData.value.productsCategory.scopes : []
  const domainScopes = domainConfig.scopes ?? []

  const hasCommonScope = domainScopes.some((scope: string) => productCategoryScopes.includes(scope))

  if (!hasCommonScope) {
    pinoLogger.warn(
      {slug, domainName, locale, productCategoryScopes, domainScopes, url, traffic},
      'User redirected to 404: slug scope and domain scope do not have common scope',
    )
    throw new Response('Not Found', {status: 404})
  }
}

export default SlugIndex
